import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CalendarDays, ChevronRight, Clock3, History, Loader2, NotebookText } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { RootState, useAppDispatch } from "../../../redux/store";
import {
    getEmployeeTimeoffBalanceHistoryList,
    getEmployeeTimeoffOverview,
    getEmployeeTimeoffRequestsList,
    resetMobileTimeoffCategoryDetail,
    setSelectedTimeoffForView,
} from "../../../redux/features/timeoff";
import { getAssignedTimeoffCategories } from "../../../redux/features/common";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { clearGlobalBackHandler, setGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { DateTimeUtil, formatHoursRounded } from "../../../utils";
import { getApprovalStatusCheck, getApprovalStatusText } from "../../../utils/timeoff";
import { Timeoff, TimeoffBalanceHistory, TimeoffCategoryType } from "../../../types";

type ActiveTab = "requests" | "history";
type SelectOption = {
    label: string;
    value: string;
};

type LocationState = {
    category?: TimeoffCategoryType;
    employeeId?: string;
    employeeName?: string;
    from?: string;
    policyCode?: string;
    activeTab?: ActiveTab;
    selectedYear?: number;
};

const PAGE_LIMIT = 20;
const UNASSIGNED_POLICY = "__UNASSIGNED_POLICY__";
const CURRENT_YEAR = new Date().getFullYear();

const getPolicyKey = (policyCode?: string | null): string => policyCode || UNASSIGNED_POLICY;
const getPolicyLabel = (policyCode?: string | null): string => policyCode || "Assigned policy";
const getYearRange = (): number[] => Array.from({ length: 6 }, (_, index) => CURRENT_YEAR - index);
const getYearBounds = (year: number) => ({
    start_date: `${year}-01-01`,
    end_date: `${year}-12-31`,
});

const safeDecode = (value?: string): string => {
    if (!value) return "";

    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const formatDate = (date?: string): string => {
    if (!date) return "Unknown date";
    return DateTimeUtil.formatDate(date, "MMM d, yyyy");
};

const formatAmountChange = (record: TimeoffBalanceHistory): string => {
    const amount = Number(record.amount ?? 0);
    const isLess = String(record.action ?? "").toLowerCase() === "less" || amount < 0;
    const prefix = isLess ? "-" : "+";

    return `${prefix}${formatHoursRounded(Math.abs(amount))}`;
};

const getBalanceHistoryRecordId = (record: TimeoffBalanceHistory): string =>
    record.os_emp_time_off_balance_history || record.balance_history_id || "";

const getCategoryIconComponent = (iconName?: string) => {
    if (!iconName) return null;

    const pascalCase = iconName
        .split(/[-_]/g)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("");

    const Icon = LucideIcons[pascalCase as keyof typeof LucideIcons];
    return Icon as React.FC<React.SVGProps<SVGSVGElement>> | null;
};

const selectStyles = {
    control: (baseStyles: any, state: any) => ({
        ...baseStyles,
        backgroundColor: "white",
        borderColor: state.isFocused ? "#60a5fa" : "#e2e8f0",
        borderRadius: "1rem",
        boxShadow: "none",
        minHeight: 56,
        paddingLeft: 4,
        ":hover": {
            borderColor: state.isFocused ? "#60a5fa" : "#cbd5e1",
        },
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    menuPortal: (baseStyles: any) => ({
        ...baseStyles,
        zIndex: 9999,
    }),
    menu: (baseStyles: any) => ({
        ...baseStyles,
        border: "1px solid #e2e8f0",
        borderRadius: "1rem",
        overflow: "hidden",
    }),
    option: (baseStyles: any, state: any) => ({
        ...baseStyles,
        backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "white",
        color: state.isSelected ? "white" : "#0f172a",
        cursor: "pointer",
    }),
    placeholder: (baseStyles: any) => ({
        ...baseStyles,
        color: "#94a3b8",
    }),
    valueContainer: (baseStyles: any) => ({
        ...baseStyles,
        paddingLeft: 0,
    }),
} as const;

const TimeoffCategoryDetail: React.FC = () => {
    const { categoryCode: categoryCodeParam, employeeId: employeeIdParam } = useParams<{
        categoryCode?: string;
        employeeId?: string;
    }>();
    const routeCategoryCode = safeDecode(categoryCodeParam);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { setSourcePath } = useNavigationHistory();
    const locationState = (location.state || {}) as LocationState;
    const [activeTab, setActiveTab] = useState<ActiveTab>("requests");
    const [selectedPolicyKey, setSelectedPolicyKey] = useState<string>("");
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<string>(routeCategoryCode);
    const [selectedYear, setSelectedYear] = useState<number>(locationState.selectedYear || CURRENT_YEAR);
    const [isOverviewResolved, setIsOverviewResolved] = useState(false);
    const latestOverviewRequestKeyRef = React.useRef("");

    const user = useSelector((state: RootState) => state.user.user);
    const { assignedTimeoffCategories, isLoadingAssignedCategories } = useSelector((state: RootState) => state.common);
    const {
        isLoadingMobileCategoryOverview,
        isLoadingMobileCategoryRequests,
        isLoadingMoreMobileCategoryRequests,
        mobileCategoryRequests,
        mobileCategoryRequestsMeta,
        mobileCategoryOverviewError,
        mobileCategoryRequestsError,
        isLoadingMobileCategoryBalanceHistory,
        isLoadingMoreMobileCategoryBalanceHistory,
        mobileCategoryBalanceHistory,
        mobileCategoryBalanceHistoryMeta,
        mobileCategoryBalanceHistoryError,
        mobileTimeoffCategory,
        mobileTimeoffCategoryBalance,
    } = useSelector((state: RootState) => state.timeoff);

    const employeeId = employeeIdParam || locationState.employeeId || user?.id?.toString();
    const backPath = locationState.from || (employeeIdParam ? `/m-manager-timeoff/${employeeIdParam}` : "/m-timeoff");
    const isManagerMode = Boolean(employeeIdParam);
    const activeCategoryCode = selectedCategoryCode || routeCategoryCode;
    const selectedPolicyCode = selectedPolicyKey && selectedPolicyKey !== UNASSIGNED_POLICY ? selectedPolicyKey : "";
    const yearOptions = React.useMemo(getYearRange, []);
    const yearSelectOptions = React.useMemo<SelectOption[]>(
        () =>
            yearOptions.map((year) => ({
                label: String(year),
                value: String(year),
            })),
        [yearOptions]
    );
    const policyOptions = React.useMemo(() => {
        const optionsByKey = new Map<string, { value: string; label: string }>();

        assignedTimeoffCategories.forEach((category) => {
            if (!category.category_code) return;
            const key = getPolicyKey(category.policy_code);
            if (!optionsByKey.has(key)) {
                optionsByKey.set(key, {
                    value: key,
                    label: getPolicyLabel(category.policy_code),
                });
            }
        });

        return Array.from(optionsByKey.values());
    }, [assignedTimeoffCategories]);
    const categoryOptions = React.useMemo(() => {
        const optionsByCode = new Map<string, TimeoffCategoryType>();

        assignedTimeoffCategories
            .filter((category) => !selectedPolicyKey || getPolicyKey(category.policy_code) === selectedPolicyKey)
            .forEach((category) => {
                if (category.category_code && !optionsByCode.has(category.category_code)) {
                    optionsByCode.set(category.category_code, category);
                }
            });

        return Array.from(optionsByCode.values());
    }, [assignedTimeoffCategories, selectedPolicyKey]);
    const categoryFromState = locationState.category?.category_code === activeCategoryCode ? locationState.category : undefined;
    const categoryFromAssigned = assignedTimeoffCategories.find(
        (category) =>
            category.category_code === activeCategoryCode &&
            (!selectedPolicyKey || getPolicyKey(category.policy_code) === selectedPolicyKey)
    );
    const categoryFromApi = mobileTimeoffCategory?.category_code === activeCategoryCode ? mobileTimeoffCategory : undefined;
    const category = categoryFromApi ?? categoryFromState ?? categoryFromAssigned;
    const balance =
        categoryFromApi && mobileTimeoffCategoryBalance
            ? mobileTimeoffCategoryBalance
            : {
                  amount: category?.time_off_hours_balance,
                  unit: category?.track_time_in,
                  last_update: undefined,
              };
    const categoryIconName = categoryFromApi?.icon || undefined;
    const categoryColorClass = categoryFromApi?.calendar_color || "";
    const categoryCardClass = categoryColorClass.startsWith("bg-") ? categoryColorClass : "bg-blue-600";
    const isWaitingForOverviewTheme =
        Boolean(activeCategoryCode && employeeId) && !isOverviewResolved && !mobileCategoryOverviewError;
    const activeTabClass = isWaitingForOverviewTheme
        ? "bg-slate-100 text-slate-500 shadow-sm"
        : `${categoryCardClass} text-white shadow-sm`;
    const CategoryIcon = getCategoryIconComponent(categoryIconName);

    useEffect(() => {
        dispatch(resetMobileTimeoffCategoryDetail());

        return () => {
            dispatch(resetMobileTimeoffCategoryDetail());
        };
    }, [activeCategoryCode, dispatch, selectedPolicyCode, selectedYear]);

    useEffect(() => {
        if (employeeId) {
            dispatch(getAssignedTimeoffCategories({ employee_id: employeeId }));
        }
    }, [dispatch, employeeId]);

    useEffect(() => {
        if (routeCategoryCode) {
            setSelectedCategoryCode(routeCategoryCode);
            return;
        }

        if (locationState.category?.category_code) {
            setSelectedCategoryCode(locationState.category.category_code);
        }
    }, [locationState.category?.category_code, routeCategoryCode]);

    useEffect(() => {
        if (locationState.activeTab === "requests" || locationState.activeTab === "history") {
            setActiveTab(locationState.activeTab);
        }
    }, [locationState.activeTab]);

    useEffect(() => {
        if (locationState.selectedYear) {
            setSelectedYear(locationState.selectedYear);
        }
    }, [locationState.selectedYear]);

    useEffect(() => {
        const statePolicyCode = locationState.policyCode || locationState.category?.policy_code;
        if (statePolicyCode) {
            setSelectedPolicyKey(getPolicyKey(statePolicyCode));
            return;
        }

        const categoryPolicyCode = activeCategoryCode
            ? assignedTimeoffCategories.find((category) => category.category_code === activeCategoryCode)?.policy_code
            : undefined;
        if (categoryPolicyCode) {
            const categoryPolicyKey = getPolicyKey(categoryPolicyCode);
            if (
                (routeCategoryCode && selectedPolicyKey !== categoryPolicyKey) ||
                !selectedPolicyKey ||
                !policyOptions.some((policy) => policy.value === selectedPolicyKey)
            ) {
                setSelectedPolicyKey(categoryPolicyKey);
            }
            return;
        }

        if (
            isManagerMode &&
            policyOptions.length &&
            (!selectedPolicyKey || !policyOptions.some((policy) => policy.value === selectedPolicyKey))
        ) {
            setSelectedPolicyKey(policyOptions[0].value);
        }
    }, [
        activeCategoryCode,
        assignedTimeoffCategories,
        isManagerMode,
        locationState.category?.policy_code,
        locationState.policyCode,
        policyOptions,
        routeCategoryCode,
        selectedPolicyKey,
    ]);

    useEffect(() => {
        if (routeCategoryCode) return;

        if (!categoryOptions.length) {
            setSelectedCategoryCode("");
            return;
        }

        if (!selectedCategoryCode || !categoryOptions.some((category) => category.category_code === selectedCategoryCode)) {
            setSelectedCategoryCode(categoryOptions[0].category_code || "");
        }
    }, [categoryOptions, routeCategoryCode, selectedCategoryCode]);

    useEffect(() => {
        const title = isManagerMode
            ? locationState.employeeName
                ? `${locationState.employeeName} Time Off`
                : "Employee Time Off"
            : category?.category_name || "Time Off";
        dispatch(setTopNavNameTitle(title));
        setSourcePath(backPath);

        setGlobalBackHandler(() => {
            navigate(backPath);
            return true;
        });

        return () => {
            clearGlobalBackHandler();
        };
    }, [backPath, category?.category_name, dispatch, isManagerMode, locationState.employeeName, navigate, setSourcePath]);

    useEffect(() => {
        if (!activeCategoryCode || !employeeId || (isManagerMode && !selectedPolicyKey)) return;

        const requestKey = `${activeCategoryCode}::${employeeId}::${selectedPolicyCode || ""}::${selectedYear}`;
        latestOverviewRequestKeyRef.current = requestKey;
        setIsOverviewResolved(false);

        dispatch(
            getEmployeeTimeoffOverview({
                category_code: activeCategoryCode,
                policy_code: selectedPolicyCode || undefined,
                employee_id: employeeId,
                ...getYearBounds(selectedYear),
            })
        ).finally(() => {
            if (latestOverviewRequestKeyRef.current === requestKey) {
                setIsOverviewResolved(true);
            }
        });
    }, [activeCategoryCode, dispatch, employeeId, isManagerMode, selectedPolicyCode, selectedPolicyKey, selectedYear]);

    useEffect(() => {
        if (!activeCategoryCode || !employeeId || (isManagerMode && !selectedPolicyKey)) return;

        const payload = {
            category_code: activeCategoryCode,
            policy_code: selectedPolicyCode || undefined,
            employee_id: employeeId,
            page: 1,
            limit: PAGE_LIMIT,
            ...getYearBounds(selectedYear),
        };

        if (activeTab === "requests") {
            dispatch(getEmployeeTimeoffRequestsList(payload));
        } else {
            dispatch(getEmployeeTimeoffBalanceHistoryList(payload));
        }
    }, [activeCategoryCode, activeTab, dispatch, employeeId, isManagerMode, selectedPolicyCode, selectedPolicyKey, selectedYear]);

    const loadMore = () => {
        if (!activeCategoryCode || !employeeId || (isManagerMode && !selectedPolicyKey)) return;

        if (activeTab === "requests" && mobileCategoryRequestsMeta?.has_more) {
            dispatch(
                getEmployeeTimeoffRequestsList({
                    category_code: activeCategoryCode,
                    policy_code: selectedPolicyCode || undefined,
                    employee_id: employeeId,
                    page: Number(mobileCategoryRequestsMeta.page ?? 1) + 1,
                    limit: Number(mobileCategoryRequestsMeta.limit ?? PAGE_LIMIT),
                    ...getYearBounds(selectedYear),
                })
            );
        }

        if (activeTab === "history" && mobileCategoryBalanceHistoryMeta?.has_more) {
            dispatch(
                getEmployeeTimeoffBalanceHistoryList({
                    category_code: activeCategoryCode,
                    policy_code: selectedPolicyCode || undefined,
                    employee_id: employeeId,
                    page: Number(mobileCategoryBalanceHistoryMeta.page ?? 1) + 1,
                    limit: Number(mobileCategoryBalanceHistoryMeta.limit ?? PAGE_LIMIT),
                    ...getYearBounds(selectedYear),
                })
            );
        }
    };

    const retryActiveTab = () => {
        if (!activeCategoryCode || !employeeId || (isManagerMode && !selectedPolicyKey)) return;

        dispatch(
            getEmployeeTimeoffOverview({
                category_code: activeCategoryCode,
                policy_code: selectedPolicyCode || undefined,
                employee_id: employeeId,
                ...getYearBounds(selectedYear),
            })
        );

        const payload = {
            category_code: activeCategoryCode,
            policy_code: selectedPolicyCode || undefined,
            employee_id: employeeId,
            page: 1,
            limit: PAGE_LIMIT,
            ...getYearBounds(selectedYear),
        };

        if (activeTab === "requests") {
            dispatch(getEmployeeTimeoffRequestsList(payload));
        } else {
            dispatch(getEmployeeTimeoffBalanceHistoryList(payload));
        }
    };

    const navToTimeoffDetails = (request: Timeoff) => {
        dispatch(setSelectedTimeoffForView(request));
        const from = employeeIdParam
            ? `/m-manager-timeoff/${employeeIdParam}`
            : `/m-timeoff-category/${encodeURIComponent(activeCategoryCode)}`;
        navigate("/m-timeoff-details", {
            state: {
                from,
                fromState: {
                    activeTab,
                    category: category ?? {
                        category_code: activeCategoryCode,
                        policy_code: selectedPolicyCode || undefined,
                    },
                    employeeId,
                    employeeName: locationState.employeeName,
                    from: employeeIdParam ? "/m-manager-timeoff" : "/m-timeoff",
                    policyCode: selectedPolicyCode || category?.policy_code,
                    selectedYear,
                },
                category,
                employeeId,
                employeeName: locationState.employeeName,
                readOnly: Boolean(employeeIdParam || locationState.employeeId),
            },
        });
    };

    const handlePolicyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextPolicyKey = event.target.value;
        const nextCategory = assignedTimeoffCategories.find(
            (category) => category.category_code && getPolicyKey(category.policy_code) === nextPolicyKey
        );

        setSelectedPolicyKey(nextPolicyKey);
        setSelectedCategoryCode(nextCategory?.category_code || "");
        setActiveTab("requests");
    };

    const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCategoryCode(event.target.value);
        setActiveTab("requests");
    };

    const handleYearChange = (option: SelectOption | null) => {
        setSelectedYear(Number(option?.value || CURRENT_YEAR));
        setActiveTab("requests");
    };

    const isRequestsTab = activeTab === "requests";
    const isInitialLoading =
        isLoadingMobileCategoryOverview || (isRequestsTab ? isLoadingMobileCategoryRequests : isLoadingMobileCategoryBalanceHistory);
    const isLoadingMore = isRequestsTab
        ? isLoadingMoreMobileCategoryRequests
        : isLoadingMoreMobileCategoryBalanceHistory;
    const activeError = mobileCategoryOverviewError || (isRequestsTab ? mobileCategoryRequestsError : mobileCategoryBalanceHistoryError);
    const hasMore = isRequestsTab
        ? Boolean(mobileCategoryRequestsMeta?.has_more)
        : Boolean(mobileCategoryBalanceHistoryMeta?.has_more);

    if (!activeCategoryCode && !isManagerMode) {
        return (
            <div className="min-h-full bg-slate-50 px-4 py-6">
                <div className="mx-auto max-w-md rounded-2xl bg-white p-5 text-center shadow-sm">
                    <p className="font-semibold text-slate-900">Time off category is missing.</p>
                    <button
                        className="mt-4 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
                        onClick={() => navigate(backPath)}
                    >
                        Back to Time Off
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full bg-slate-50 px-3 py-4 sm:px-5 lg:px-8">
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
                {isManagerMode && (
                    <section className="rounded-3xl bg-white p-4 shadow-sm sm:p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Select Policy & Category
                        </p>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="block">
                                <span className="mb-1 block text-xs font-semibold text-slate-500">Policy</span>
                                <select
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none focus:border-blue-500"
                                    disabled={!policyOptions.length}
                                    value={selectedPolicyKey}
                                    onChange={handlePolicyChange}
                                >
                                    {!policyOptions.length ? (
                                        <option value="">No policies assigned</option>
                                    ) : (
                                        policyOptions.map((policy) => (
                                            <option key={policy.value} value={policy.value}>
                                                {policy.label}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-xs font-semibold text-slate-500">Category</span>
                                <select
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none focus:border-blue-500"
                                    disabled={!categoryOptions.length}
                                    value={activeCategoryCode}
                                    onChange={handleCategoryChange}
                                >
                                    {!categoryOptions.length ? (
                                        <option value="">No categories assigned</option>
                                    ) : (
                                        categoryOptions.map((categoryOption) => (
                                            <option key={categoryOption.category_code} value={categoryOption.category_code}>
                                                {categoryOption.category_name || categoryOption.category_code}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </label>
                        </div>
                    </section>
                )}

                {activeCategoryCode && (
                    isWaitingForOverviewTheme ? (
                        <section className="flex min-h-[208px] items-center justify-center rounded-3xl bg-white shadow-sm">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </section>
                    ) : (
                        <section className={`overflow-hidden rounded-3xl ${categoryCardClass} text-white shadow-sm`}>
                            <div className="p-5 sm:p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-100">
                                            Time Off Category
                                        </p>
                                        <h1 className="mt-2 truncate text-2xl font-bold sm:text-3xl">
                                            {category?.category_name || activeCategoryCode}
                                        </h1>
                                        <p className="mt-1 text-sm text-blue-50">
                                            {category?.track_time_in ? `Tracked in ${category.track_time_in}` : "Category details"}
                                        </p>
                                    </div>
                                    <div
                                        className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${categoryCardClass}`}
                                    >
                                        {CategoryIcon ? (
                                            <CategoryIcon className="h-6 w-6 text-white" />
                                        ) : (
                                            <Clock3 className="h-6 w-6 text-white" />
                                        )}
                                    </div>
                                </div>

                                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <div className="rounded-2xl bg-white/15 p-4 backdrop-blur">
                                        <p className="text-xs text-blue-50">Current balance</p>
                                        <p className="mt-1 text-2xl font-bold">
                                            {balance.amount !== undefined && balance.amount !== null
                                                ? formatHoursRounded(balance.amount)
                                                : "N/A"}
                                        </p>
                                        <p className="text-xs text-blue-50">{balance.unit || category?.track_time_in || "Units"}</p>
                                    </div>
                                    {/* <div className="rounded-2xl bg-white/15 p-4 backdrop-blur sm:col-span-2">
                                        <p className="text-xs text-blue-50">Last updated</p>
                                        <p className="mt-1 truncate text-base font-semibold">
                                            {balance.last_update ? formatDate(balance.last_update) : "Newest records first"}
                                        </p>
                                        <p className="mt-1 text-xs text-blue-50">
                                            Viewing {selectedYear}
                                        </p>
                                    </div> */}
                                </div>
                            </div>
                        </section>
                    )
                )}

                <div className="rounded-2xl bg-white p-1 shadow-sm">
                    <div className="grid grid-cols-2 gap-1">
                        <button
                            className={`rounded-xl px-3 py-3 text-xs font-semibold leading-tight transition sm:text-sm ${
                                activeTab === "requests"
                                    ? activeTabClass
                                    : "text-slate-600 hover:bg-slate-50"
                            }`}
                            onClick={() => setActiveTab("requests")}
                        >
                            Timeoff Requests
                        </button>
                        <button
                            className={`rounded-xl px-3 py-3 text-xs font-semibold leading-tight transition sm:text-sm ${
                                activeTab === "history"
                                    ? activeTabClass
                                    : "text-slate-600 hover:bg-slate-50"
                            }`}
                            onClick={() => setActiveTab("history")}
                        >
                            Timeoff Balance History
                        </button>
                    </div>
                </div>

                <div className="flex justify-end">
                    <label className="block w-full max-w-[9rem]">
                        <span className="mb-1 block text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Year
                        </span>
                        <Select
                            classNamePrefix="react-select"
                            components={{ IndicatorSeparator: () => null }}
                            isSearchable
                            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                            options={yearSelectOptions}
                            styles={selectStyles}
                            value={yearSelectOptions.find((option) => option.value === String(selectedYear)) || null}
                            onChange={(option) => handleYearChange(option as SelectOption | null)}
                        />
                    </label>
                </div>

                {isLoadingAssignedCategories && !category && (
                    <div className="rounded-2xl bg-white p-4 text-sm text-slate-500 shadow-sm">
                        Resolving category details...
                    </div>
                )}

                {!activeCategoryCode ? (
                    <EmptyState
                        icon={<CalendarDays className="h-6 w-6" />}
                        title="No category selected"
                        message="Select a policy and category to review time off requests."
                    />
                ) : isInitialLoading ? (
                    <div className="flex min-h-[240px] items-center justify-center rounded-3xl bg-white shadow-sm">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                ) : activeError ? (
                    <div className="rounded-3xl bg-white p-6 text-center shadow-sm">
                        <p className="font-semibold text-slate-900">{activeError}</p>
                        <button
                            className="mt-4 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
                            onClick={retryActiveTab}
                        >
                            Try again
                        </button>
                    </div>
                ) : activeTab === "requests" ? (
                    <RequestList records={mobileCategoryRequests} onOpenRequest={navToTimeoffDetails} />
                ) : (
                    <HistoryList records={mobileCategoryBalanceHistory} unit={balance.unit || category?.track_time_in} />
                )}

                {!isInitialLoading && !activeError && hasMore && (
                    <button
                        className="mb-6 flex w-full items-center justify-center rounded-2xl border border-blue-200 bg-white px-4 py-3 text-sm font-semibold text-blue-700 shadow-sm disabled:opacity-60"
                        disabled={isLoadingMore}
                        onClick={loadMore}
                    >
                        {isLoadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Load more
                    </button>
                )}
            </div>
        </div>
    );
};

const RequestList = ({
    records,
    onOpenRequest,
}: {
    records: Timeoff[];
    onOpenRequest: (request: Timeoff) => void;
}) => {
    if (!records.length) {
        return (
            <EmptyState
                icon={<CalendarDays className="h-6 w-6" />}
                title="No timeoff requests"
                message="Requests for this category will appear here once they are submitted."
            />
        );
    }

    return (
        <section className="space-y-3">
            {records.map((request, index) => (
                <button
                    key={`${request.time_off_request_id}-${index}`}
                    className="flex w-full items-center justify-between gap-3 rounded-2xl bg-white p-4 text-left shadow-sm transition active:scale-[0.99] sm:p-5"
                    onClick={() => onOpenRequest(request)}
                >
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-50">
                            {getApprovalStatusCheck(request.status)}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate font-semibold text-slate-900">
                                    {DateTimeUtil.formatDateRangeFancy(request.start_date!, request.end_date!)}
                                </p>
                                <span className={`text-xs font-semibold ${getApprovalStatusText(request.status)}`}>
                                    {request.status || "PENDING"}
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                                {formatHoursRounded(request.hours_requested)} {request.track_time_in || "Hours"}
                            </p>
                            {request.reason ? <p className="mt-2 line-clamp-2 text-sm text-slate-600">{request.reason}</p> : null}
                        </div>
                    </div>
                    <ChevronRight className="h-5 w-5 flex-shrink-0 text-slate-300" />
                </button>
            ))}
        </section>
    );
};

const HistoryList = ({ records, unit }: { records: TimeoffBalanceHistory[]; unit?: string }) => {
    if (!records.length) {
        return (
            <EmptyState
                icon={<History className="h-6 w-6" />}
                title="No balance history"
                message="Balance changes for this category will appear here."
            />
        );
    }

    return (
        <section className="space-y-3">
            {records.map((record, index) => {
                const isLess = String(record.action ?? "").toLowerCase() === "less" || Number(record.amount ?? 0) < 0;

                return (
                    <article
                        key={`${getBalanceHistoryRecordId(record)}-${index}`}
                        className="rounded-2xl bg-white p-4 shadow-sm sm:p-5"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`flex h-9 w-9 items-center justify-center rounded-2xl ${
                                            isLess ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"
                                        }`}
                                    >
                                        <NotebookText className="h-5 w-5" />
                                    </span>
                                    <div>
                                        <p className="font-semibold capitalize text-slate-900">
                                            {record.reason?.replace(/_/g, " ") || "Balance change"}
                                        </p>
                                        <p className="text-xs text-slate-500">{formatDate(record.change_date)}</p>
                                    </div>
                                </div>
                                {record.description ? (
                                    <p className="mt-3 line-clamp-3 text-sm text-slate-600">{record.description}</p>
                                ) : null}
                                {record.policy_name ? (
                                    <p className="mt-2 text-xs font-medium text-slate-400">{record.policy_name}</p>
                                ) : null}
                            </div>
                            <div className={`text-right text-lg font-bold ${isLess ? "text-red-600" : "text-green-600"}`}>
                                {formatAmountChange(record)}
                                <p className="text-xs font-semibold text-slate-400">{unit || "Units"}</p>
                            </div>
                        </div>
                    </article>
                );
            })}
        </section>
    );
};

const EmptyState = ({ icon, title, message }: { icon: React.ReactNode; title: string; message: string }) => (
    <div className="rounded-3xl bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            {icon}
        </div>
        <p className="mt-4 font-semibold text-slate-900">{title}</p>
        <p className="mt-1 text-sm text-slate-500">{message}</p>
    </div>
);

export default TimeoffCategoryDetail;
